import { gql } from '@apollo/client';
import type { DateRange, Sort, UUID } from '@cobliteam/common-types';
import graphQLRequester from '../../request/GraphQLRequester';
import { checklistsNamesTransformer, checklistsPageTransformer, checklistTransformer, templateChecklistTransformer } from './ChecklistTransformers';
import type { ChecklistAPI, ChecklistsNamesAPI, ChecklistsPageAPI } from './Definitions/APITypes';
import type { Checklist, ChecklistsNames, ChecklistsPage, NewChecklist, UpdatedChecklist } from './Definitions/Types';

export class GraphQLChecklistsRequester {
  private allChecklistAttributes = `
    id
    version
    name
    template
    template_id
    template_created_at
    template_created_by
    completed_count
    template_active
    photosPending: photos_pending
    pendingPhotosFieldCount: pending_photos_field_count
    vehicle_ids
    vehicles {
      id
      group_id
      group_path
    }
    vehicle_group_ids
    groups {
      id
      path
    }
    created_at
    created_by
    fields {
      id
      type
      required
      title
      should_request_photos
      photos_urls
      content {
        ... on CheckContent {
          checked
        }
        ... on TextContent {
          value
        }
        ... on SelectContent {
          options {
            id
            label
            checked
          }
        }
      }
    }
    context {
      app_version
      address
      group_path
      vehicle {
        device_id
        license_plate
        brand
        model
        group_id
      }
    }
  `;

  private pagedChecklistsAttributes = `
    page {
      content {
        ${this.allChecklistAttributes}
      }
      last
      total_pages
      total_elements
      number_of_elements
      number
      size
      first
      empty
    }
  `;

  public getCompletedChecklists = (
    period?: DateRange,
    page?: number,
    size?: number,
    sort?: Sort,
    inputFilter?: string,
    nameFilters?: string[],
    vehicleGroupIds?: string[],
    vehicleIdsFilters?: string[],
    activeFilter?: boolean,
    createdByFilter?: string[],
    templateIdFilter?: string
  ): Promise<ChecklistsPage> =>
    graphQLRequester
      .query<{ completedChecklists: ChecklistsPageAPI }>({
        query: gql`
          query GetCompletedChecklists($completedChecklistsToGet: GetCompletedChecklists) {
            completedChecklists(input: $completedChecklistsToGet) {
              ${this.pagedChecklistsAttributes}
            }
          }
        `,
        variables: {
          completedChecklistsToGet: {
            period,
            page,
            size,
            sort,
            inputFilter,
            nameFilters,
            vehicleGroupIdsFilters: vehicleGroupIds,
            vehicleIdsFilters: vehicleIdsFilters,
            activeFilter,
            createdByFilter,
            templateIdFilter,
          },
        },
      })
      .then((result) => checklistsPageTransformer(result.completedChecklists));

  public getTemplateChecklists = (
    vehicleGroupIds?: string[],
    inputFilter?: string,
    page?: number,
    size?: number,
    sort?: Sort,
    vehicleIdsFilters?: string[],
    activeFilter?: boolean,
    createdByFilter?: string[]
  ): Promise<ChecklistsPage> =>
    graphQLRequester
      .query<{ templateChecklists: ChecklistsPageAPI }>({
        query: gql`
        query GetTemplateChecklists($getTemplateChecklists: GetTemplateChecklists) {
          templateChecklists(input: $getTemplateChecklists) {
            ${this.pagedChecklistsAttributes}
          }
        }
      `,
        variables: {
          getTemplateChecklists: {
            vehicleGroupIdsFilters: vehicleGroupIds,
            vehicleIdsFilters: vehicleIdsFilters,
            nameFilters: inputFilter ? [inputFilter] : null,
            page,
            size,
            sort,
            activeFilter,
            createdByFilter,
          },
        },
      })
      .then((result) => checklistsPageTransformer(result.templateChecklists));

  public getChecklistsNames = (template: boolean): Promise<ChecklistsNames> =>
    graphQLRequester
      .query<{ checklistsNames: ChecklistsNamesAPI }>({
        query: gql`
          query GetChecklistsNames($checklistsNamesToGet: GetChecklistsNames) {
            checklistsNames(input: $checklistsNamesToGet) {
              names
            }
          }
        `,
        variables: { checklistsNamesToGet: { template } },
      })
      .then((result) => checklistsNamesTransformer(result.checklistsNames));

  public getChecklistById = (checklistId: UUID): Promise<Checklist> =>
    graphQLRequester
      .query<{ checklist: ChecklistAPI }>({
        query: gql`
          query GetChecklist($checklistToGet: GetChecklist) {
            checklist(input: $checklistToGet) {
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: { checklistToGet: { id: checklistId } },
      })
      .then((result) => checklistTransformer(result.checklist));

  public createChecklist = (newChecklist: NewChecklist): Promise<Checklist> =>
    graphQLRequester
      .mutate<{ addChecklist: ChecklistAPI }>({
        mutation: gql`
          mutation AddChecklist($checklistToCreate: NewChecklist) {
            addChecklist(input: $checklistToCreate) {
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: {
          checklistToCreate: templateChecklistTransformer(newChecklist),
        },
      })
      .then((result) => checklistTransformer(result.addChecklist));

  public updateChecklist = (id: UUID, updatedChecklist: UpdatedChecklist): Promise<Checklist> =>
    graphQLRequester
      .mutate<{ updateChecklist: ChecklistAPI }>({
        mutation: gql`
          mutation UpdateChecklist($checklistToUpdate: UpdateChecklist) {
            updateChecklist(input: $checklistToUpdate){
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: {
          checklistToUpdate: {
            id,
            updatedChecklist: templateChecklistTransformer(updatedChecklist),
          },
        },
      })
      .then((result) => checklistTransformer(result.updateChecklist));

  public deleteChecklist = (checklistId: UUID): Promise<UUID> =>
    graphQLRequester
      .mutate<{ deleteChecklist: { id: UUID } }>({
        mutation: gql`
          mutation DeleteChecklist($checklistToDelete: DeleteChecklist) {
            deleteChecklist(input: $checklistToDelete) {
              id
            }
          }
        `,
        variables: { checklistToDelete: { id: checklistId } },
      })
      .then((result) => result.deleteChecklist.id);

  public toggleChecklistActive = (id: UUID, active: boolean): Promise<Checklist> =>
    graphQLRequester
      .mutate<{ toggleChecklistActive: ChecklistAPI }>({
        mutation: gql`
          mutation ToggleChecklistActive($input: ToggleChecklistActive) {
            toggleChecklistActive(input: $input) {
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: {
          input: {
            id,
            active,
          },
        },
      })
      .then((result) => checklistTransformer(result.toggleChecklistActive));
}

export default GraphQLChecklistsRequester;
